import type {
  Project,
  Track,
  Region,
  Insert,
  Marker,
  Bus,
  MasterChannel,
  Transport,
  AutomationLane,
  Send,
} from './serializer.types.js';

const EMPTY_TRANSPORT: Transport = {
  tempo_bpm: 120,
  time_signature: [4, 4],
  key: 'C',
  sample_rate: 48000,
  length_seconds: 0,
  loop_enabled: false,
  loop_start: 0,
  loop_end: 0,
};

const EMPTY_MASTER: MasterChannel = {
  id: 'master',
  volume_db: 0,
  pan: 0,
  mute: false,
  inserts: [],
};

const ISO_NOW = (): string => new Date().toISOString();

export function serializeSession(daw: unknown): Project {
  if (!isObject(daw)) return emptyProject();
  const projectSource = pickProjectSource(daw);
  if (!isObject(projectSource)) return emptyProject();
  if (isOpenDawProject(projectSource)) {
    return serializeOpenDawProject(projectSource);
  }
  return {
    id: pickString(projectSource, 'id') ?? 'unknown',
    version: pickNumber(projectSource, 'schema_version') ?? pickNumber(projectSource, 'version') ?? 1,
    name: pickString(projectSource, 'name') ?? 'Untitled',
    created_at: pickString(projectSource, 'created_at') ?? ISO_NOW(),
    updated_at: pickString(projectSource, 'updated_at') ?? ISO_NOW(),
    owner_id: pickString(projectSource, 'owner_id') ?? '',
    collaborators: pickStringArray(projectSource, 'collaborators') ?? [],
    transport: pickTransport(projectSource),
    tracks: pickTracks(projectSource),
    buses: pickBuses(projectSource),
    master: pickMaster(projectSource),
    markers: pickMarkers(projectSource),
  };
}

// --- openDAW Project structural mapping --------------------------------
// When `getProject()` returns the real openDAW `Project` (see
// daw/packages/studio/core/src/project/Project.ts), the standard pick* helpers
// don't apply because openDAW exposes its state via adapters rather than plain
// dictionaries. Detect that shape and walk it directly.

function isOpenDawProject(source: Record<string, unknown>): boolean {
  return (
    isObject(source['rootBoxAdapter']) &&
    isObject(source['timelineBoxAdapter']) &&
    isObject(source['editing'])
  );
}

function serializeOpenDawProject(project: Record<string, unknown>): Project {
  const tracks = readOpenDawTracks(project);
  const markers = readOpenDawMarkers(project);
  const bpm = readOpenDawBpm(project);
  return {
    id: 'unknown',
    version: 1,
    name: 'Untitled',
    created_at: ISO_NOW(),
    updated_at: ISO_NOW(),
    owner_id: '',
    collaborators: [],
    transport: {
      tempo_bpm: bpm,
      time_signature: [4, 4],
      key: 'C',
      sample_rate: 48000,
      length_seconds: 0,
      loop_enabled: false,
      loop_start: 0,
      loop_end: 0,
    },
    tracks,
    buses: [],
    master: { ...EMPTY_MASTER },
    markers,
  };
}

function readOpenDawBpm(project: Record<string, unknown>): number {
  const timelineAdapter = project['timelineBoxAdapter'];
  if (!isObject(timelineAdapter)) return 120;
  const box = timelineAdapter['box'];
  if (!isObject(box)) return 120;
  const bpmField = box['bpm'];
  if (!isObject(bpmField) || typeof bpmField['getValue'] !== 'function') return 120;
  const value = (bpmField['getValue'] as () => unknown)();
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 120;
}

function readOpenDawTracks(project: Record<string, unknown>): Track[] {
  const rootBoxAdapter = project['rootBoxAdapter'];
  if (!isObject(rootBoxAdapter)) return [];
  const audioUnits = rootBoxAdapter['audioUnits'];
  if (!isObject(audioUnits) || typeof audioUnits['adapters'] !== 'function') return [];
  const list = (audioUnits['adapters'] as () => unknown)();
  if (!Array.isArray(list)) return [];
  const tracks: Track[] = [];
  for (const audioUnit of list) {
    const track = audioUnitToTrack(audioUnit);
    if (track !== null) tracks.push(track);
  }
  return tracks;
}

function audioUnitToTrack(audioUnit: unknown): Track | null {
  if (!isObject(audioUnit)) return null;
  const uuidBytes = audioUnit['uuid'];
  const id = uuidBytes instanceof Uint8Array ? bytesToUuid(uuidBytes) : 'unknown';
  const named = audioUnit['namedParameter'];
  const volume_db = readParamNumber(named, 'volume') ?? 0;
  const pan = readParamNumber(named, 'panning') ?? 0;
  const mute = readParamBoolean(named, 'mute') ?? false;
  const solo = readParamBoolean(named, 'solo') ?? false;
  return {
    id,
    name: readAudioUnitLabel(audioUnit) ?? 'Track',
    type: 'audio',
    color: '#888888',
    volume_db,
    pan,
    mute,
    solo,
    arm: false,
    inserts: readAudioUnitInserts(audioUnit),
    sends: [],
    output_routing: 'master',
    input_routing: null,
    regions: readAudioUnitRegions(audioUnit),
    automation: [],
    frozen: false,
  };
}

function readAudioUnitInserts(audioUnit: Record<string, unknown>): Insert[] {
  const audioEffects = audioUnit['audioEffects'];
  if (!isObject(audioEffects) || typeof audioEffects['adapters'] !== 'function') return [];
  const adapters = (audioEffects['adapters'] as () => unknown)();
  if (!Array.isArray(adapters)) return [];
  return adapters.filter(isObject).map(effectAdapterToInsert);
}

function effectAdapterToInsert(adapter: Record<string, unknown>): Insert {
  const uuidBytes = adapter['uuid'];
  const id = uuidBytes instanceof Uint8Array ? bytesToUuid(uuidBytes) : 'unknown';
  const box = adapter['box'];
  let plugin_id = 'unknown';
  if (isObject(box)) {
    const ctor = box['constructor'];
    if (isObject(ctor) && typeof ctor['name'] === 'string') {
      plugin_id = (ctor['name'] as string).replace(/DeviceBox$/, '').toLowerCase();
    }
  }
  const enabledField = adapter['enabledField'];
  let bypass = false;
  if (isObject(enabledField) && typeof enabledField['getValue'] === 'function') {
    const enabled = (enabledField['getValue'] as () => unknown)();
    bypass = enabled === false;
  }
  const namedParam = adapter['namedParameter'];
  const params: Record<string, number> = {};
  if (isObject(namedParam)) {
    for (const [key, entry] of Object.entries(namedParam)) {
      if (!isObject(entry) || typeof entry['getValue'] !== 'function') continue;
      const val = (entry['getValue'] as () => unknown)();
      if (typeof val === 'number') params[key] = val;
      else if (typeof val === 'boolean') params[key] = val ? 1 : 0;
    }
  }
  return { id, plugin_id, bypass, params };
}

// Walk audioUnit.tracks.values() → TrackBoxAdapter[] → .regions.collection.asArray()
// to read actual audio regions. position/duration are in PPQN (int32).
function readAudioUnitRegions(audioUnit: Record<string, unknown>): Region[] {
  const tracksObj = audioUnit['tracks'];
  if (!isObject(tracksObj) || typeof tracksObj['values'] !== 'function') return [];
  const trackAdapters = (tracksObj['values'] as () => unknown).call(tracksObj);
  if (!Array.isArray(trackAdapters)) return [];
  const regions: Region[] = [];
  for (const trackAdapter of trackAdapters) {
    if (!isObject(trackAdapter)) continue;
    const trackRegions = trackAdapter['regions'];
    if (!isObject(trackRegions)) continue;
    const collection = trackRegions['collection'];
    if (!isObject(collection) || typeof collection['asArray'] !== 'function') continue;
    const regionArray = (collection['asArray'] as () => unknown).call(collection);
    if (!Array.isArray(regionArray)) continue;
    for (const regionAdapter of regionArray) {
      if (!isObject(regionAdapter) || regionAdapter['type'] !== 'audio-region') continue;
      const rUuidBytes = regionAdapter['uuid'];
      const rid = rUuidBytes instanceof Uint8Array ? bytesToUuid(rUuidBytes) : 'unknown';
      const position = typeof regionAdapter['position'] === 'number' ? regionAdapter['position'] : 0;
      const duration = typeof regionAdapter['duration'] === 'number' ? regionAdapter['duration'] : 0;
      regions.push({
        id: rid,
        audio_blob_hash: readRegionBlobHash(regionAdapter),
        start: position,
        offset: 0,
        length: duration,
        gain_db: 0,
        fade_in_ms: 0,
        fade_out_ms: 0,
        fade_curve: 'linear',
        loop: false,
        reversed: false,
        phase_inverted: false,
        time_stretch_factor: 1.0,
        time_stretch_algorithm: 'soundtouch',
      });
    }
  }
  return regions;
}

function readRegionBlobHash(region: Record<string, unknown>): string {
  // AudioRegionBoxAdapter.box → AudioRegionBox.file (PointerField) → targetVertex (Option<Vertex>)
  // → Vertex.box (AudioFileBox) → address.uuid (Uint8Array)
  // Audio is stored in OPFS at samples/v2/{UUID}/audio.wav, so the UUID is the lookup key.
  const box = region['box'];
  if (!isObject(box)) return '';
  const fileField = box['file'];
  if (!isObject(fileField)) return '';
  const targetVertex = fileField['targetVertex'];
  if (!isObject(targetVertex)) return '';
  const vertex = tryUnwrap(targetVertex);
  if (!isObject(vertex)) return '';
  const audioFileBox = vertex['box'];
  if (!isObject(audioFileBox)) return '';
  const address = audioFileBox['address'];
  if (!isObject(address)) return '';
  const uuidBytes = address['uuid'];
  if (uuidBytes instanceof Uint8Array) return bytesToUuid(uuidBytes);
  return '';
}

function tryUnwrap(opt: Record<string, unknown>): unknown {
  if (typeof opt['unwrap'] === 'function') {
    try {
      return (opt['unwrap'] as () => unknown).call(opt);
    } catch {
      return undefined;
    }
  }
  return opt['value'] ?? opt['_value'];
}

function readParamNumber(named: unknown, key: string): number | undefined {
  if (!isObject(named)) return undefined;
  const param = named[key];
  if (!isObject(param)) return undefined;
  const getter = param['getValue'];
  if (typeof getter !== 'function') return undefined;
  const value = (getter as () => unknown).call(param);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readParamBoolean(named: unknown, key: string): boolean | undefined {
  if (!isObject(named)) return undefined;
  const param = named[key];
  if (!isObject(param)) return undefined;
  const getter = param['getValue'];
  if (typeof getter !== 'function') return undefined;
  const value = (getter as () => unknown).call(param);
  return typeof value === 'boolean' ? value : undefined;
}

function readAudioUnitLabel(audioUnit: Record<string, unknown>): string | undefined {
  const input = audioUnit['input'];
  if (!isObject(input) || typeof input['adapter'] !== 'function') return undefined;
  const opt = (input['adapter'] as () => unknown).call(input);
  if (!isObject(opt) || typeof opt['unwrap'] !== 'function') return undefined;
  let inputAdapter: unknown;
  try {
    inputAdapter = (opt['unwrap'] as () => unknown).call(opt);
  } catch {
    return undefined;
  }
  if (!isObject(inputAdapter)) return undefined;
  const label = inputAdapter['labelField'];
  if (!isObject(label) || typeof label['getValue'] !== 'function') return undefined;
  const value = (label['getValue'] as () => unknown).call(label);
  return typeof value === 'string' ? value : undefined;
}

function readOpenDawMarkers(project: Record<string, unknown>): Marker[] {
  const timelineAdapter = project['timelineBoxAdapter'];
  if (!isObject(timelineAdapter)) return [];
  const markerTrack = timelineAdapter['markerTrack'];
  if (!isObject(markerTrack)) return [];
  const events = markerTrack['events'];
  if (!isObject(events)) return [];
  // EventCollection exposes `asArray()`/`*[Symbol.iterator]()`. Try array.
  const list = readEventsAsArray(events);
  if (list === null) return [];
  const bpm = readOpenDawBpm(project);
  const out: Marker[] = [];
  for (const marker of list) {
    if (!isObject(marker)) continue;
    const position = typeof marker['position'] === 'number' ? marker['position'] : 0;
    const labelValue = marker['label'];
    const uuidBytes = marker['uuid'];
    out.push({
      id: uuidBytes instanceof Uint8Array ? bytesToUuid(uuidBytes) : 'unknown',
      name: typeof labelValue === 'string' ? labelValue : 'Marker',
      position_seconds: ppqnToSeconds(position, bpm),
    });
  }
  return out;
}

function readEventsAsArray(events: Record<string, unknown>): Iterable<unknown> | null {
  if (typeof events['asArray'] === 'function') {
    const arr = (events['asArray'] as () => unknown).call(events);
    if (Array.isArray(arr)) return arr;
  }
  const iterator = (events as Record<string | symbol, unknown>)[Symbol.iterator];
  if (typeof iterator === 'function') {
    return events as unknown as Iterable<unknown>;
  }
  return null;
}

// PPQN.Quarter = 960. See daw/packages/lib/dsp/src/ppqn.ts L11+L32.
const PPQN_QUARTER = 960;
function ppqnToSeconds(position: number, bpm: number): number {
  if (bpm <= 0) return 0;
  return (position * 60.0) / PPQN_QUARTER / bpm;
}

const HEX_TABLE: ReadonlyArray<string> = (() => {
  const out: string[] = [];
  for (let i = 0; i < 256; i++) out.push((i + 0x100).toString(16).slice(1));
  return out;
})();

export function bytesToUuid(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    return Array.from(bytes).map((byte) => HEX_TABLE[byte]).join('');
  }
  return (
    HEX_TABLE[bytes[0]] + HEX_TABLE[bytes[1]] + HEX_TABLE[bytes[2]] + HEX_TABLE[bytes[3]] + '-' +
    HEX_TABLE[bytes[4]] + HEX_TABLE[bytes[5]] + '-' +
    HEX_TABLE[bytes[6]] + HEX_TABLE[bytes[7]] + '-' +
    HEX_TABLE[bytes[8]] + HEX_TABLE[bytes[9]] + '-' +
    HEX_TABLE[bytes[10]] + HEX_TABLE[bytes[11]] +
    HEX_TABLE[bytes[12]] + HEX_TABLE[bytes[13]] +
    HEX_TABLE[bytes[14]] + HEX_TABLE[bytes[15]]
  );
}

function emptyProject(): Project {
  return {
    id: 'unknown',
    version: 1,
    name: 'Untitled',
    created_at: ISO_NOW(),
    updated_at: ISO_NOW(),
    owner_id: '',
    collaborators: [],
    transport: { ...EMPTY_TRANSPORT },
    tracks: [],
    buses: [],
    master: { ...EMPTY_MASTER },
    markers: [],
  };
}

function pickProjectSource(daw: Record<string, unknown>): unknown {
  if (typeof daw['getProject'] === 'function') {
    try {
      return (daw['getProject'] as () => unknown)();
    } catch {
      return undefined;
    }
  }
  return daw['project'] ?? daw;
}

function pickTransport(src: Record<string, unknown>): Transport {
  const t = isObject(src['transport']) ? src['transport'] : src;
  if (!isObject(t)) return { ...EMPTY_TRANSPORT };
  const ts = pickArray(t, 'time_signature');
  return {
    tempo_bpm: pickNumber(t, 'tempo_bpm') ?? pickNumber(t, 'bpm') ?? 120,
    time_signature: ts && ts.length === 2 && typeof ts[0] === 'number' && typeof ts[1] === 'number'
      ? [ts[0], ts[1]]
      : [4, 4],
    key: pickString(t, 'key') ?? 'C',
    sample_rate: 48000,
    length_seconds: pickNumber(t, 'length_seconds') ?? pickNumber(t, 'duration') ?? 0,
    loop_enabled: pickBoolean(t, 'loop_enabled') ?? false,
    loop_start: pickNumber(t, 'loop_start') ?? 0,
    loop_end: pickNumber(t, 'loop_end') ?? 0,
  };
}

function pickTracks(src: Record<string, unknown>): Track[] {
  const raw = pickArray(src, 'tracks') ?? [];
  return raw.filter(isObject).map(toTrack);
}

function toTrack(t: Record<string, unknown>): Track {
  return {
    id: pickString(t, 'id') ?? 'unknown',
    name: pickString(t, 'name') ?? 'Track',
    type: pickTrackType(t),
    color: pickString(t, 'color') ?? '#888888',
    volume_db: pickNumber(t, 'volume_db') ?? 0,
    pan: pickNumber(t, 'pan') ?? 0,
    mute: pickBoolean(t, 'mute') ?? false,
    solo: pickBoolean(t, 'solo') ?? false,
    arm: pickBoolean(t, 'arm') ?? false,
    inserts: pickInserts(t),
    sends: pickSends(t),
    output_routing: pickString(t, 'output_routing') ?? 'master',
    input_routing: pickString(t, 'input_routing') ?? null,
    regions: pickRegions(t),
    automation: pickAutomation(t),
    frozen: pickBoolean(t, 'frozen') ?? false,
    ...(typeof t['frozen_blob_hash'] === 'string' ? { frozen_blob_hash: t['frozen_blob_hash'] as string } : {}),
  };
}

function pickTrackType(t: Record<string, unknown>): Track['type'] {
  const raw = pickString(t, 'type');
  if (raw === 'audio' || raw === 'midi' || raw === 'instrument') return raw;
  return 'audio';
}

function pickInserts(t: Record<string, unknown>): Insert[] {
  const raw = pickArray(t, 'inserts') ?? [];
  return raw.filter(isObject).map((entry) => ({
    id: pickString(entry, 'id') ?? 'unknown',
    plugin_id: pickString(entry, 'plugin_id') ?? 'unknown',
    bypass: pickBoolean(entry, 'bypass') ?? false,
    params: isObject(entry['params']) ? Object.fromEntries(Object.entries(entry['params']).filter(([, v]) => typeof v === 'number')) as { [k: string]: number } : {},
    ...(typeof entry['preset_name'] === 'string' ? { preset_name: entry['preset_name'] as string } : {}),
  }));
}

function pickSends(t: Record<string, unknown>): Send[] {
  const raw = pickArray(t, 'sends') ?? [];
  return raw.filter(isObject).map((entry) => ({
    id: pickString(entry, 'id') ?? 'unknown',
    bus_id: pickString(entry, 'bus_id') ?? 'unknown',
    level_db: pickNumber(entry, 'level_db') ?? 0,
    pan: pickNumber(entry, 'pan') ?? 0,
    pre_fader: pickBoolean(entry, 'pre_fader') ?? false,
    bypass: pickBoolean(entry, 'bypass') ?? false,
  }));
}

function pickRegions(t: Record<string, unknown>): Region[] {
  const raw = pickArray(t, 'regions') ?? [];
  return raw.filter(isObject).map((entry) => ({
    id: pickString(entry, 'id') ?? 'unknown',
    audio_blob_hash: pickString(entry, 'audio_blob_hash') ?? '',
    start: pickNumber(entry, 'start') ?? 0,
    offset: pickNumber(entry, 'offset') ?? 0,
    length: pickNumber(entry, 'length') ?? 0,
    gain_db: pickNumber(entry, 'gain_db') ?? 0,
    fade_in_ms: pickNumber(entry, 'fade_in_ms') ?? 0,
    fade_out_ms: pickNumber(entry, 'fade_out_ms') ?? 0,
    fade_curve: pickFadeCurve(entry),
    loop: pickBoolean(entry, 'loop') ?? false,
    reversed: pickBoolean(entry, 'reversed') ?? false,
    phase_inverted: pickBoolean(entry, 'phase_inverted') ?? false,
    time_stretch_factor: pickNumber(entry, 'time_stretch_factor') ?? 1.0,
    time_stretch_algorithm: pickStretchAlgo(entry),
  }));
}

function pickFadeCurve(r: Record<string, unknown>): Region['fade_curve'] {
  const raw = pickString(r, 'fade_curve');
  if (raw === 'linear' || raw === 'exp' || raw === 'log' || raw === 'scurve') return raw;
  return 'linear';
}

function pickStretchAlgo(r: Record<string, unknown>): Region['time_stretch_algorithm'] {
  const raw = pickString(r, 'time_stretch_algorithm');
  if (raw === 'soundtouch' || raw === 'rubberband' || raw === 'neural') return raw;
  return 'soundtouch';
}

function pickAutomation(t: Record<string, unknown>): AutomationLane[] {
  const raw = pickArray(t, 'automation') ?? [];
  return raw.filter(isObject).map((entry) => ({
    param_path: pickString(entry, 'param_path') ?? '',
    mode: pickAutomationMode(entry),
    points: pickAutomationPoints(entry),
  }));
}

function pickAutomationMode(entry: Record<string, unknown>): AutomationLane['mode'] {
  const raw = pickString(entry, 'mode');
  if (raw === 'read' || raw === 'touch' || raw === 'latch' || raw === 'write' || raw === 'off') return raw;
  return 'read';
}

function pickAutomationPoints(entry: Record<string, unknown>): AutomationLane['points'] {
  const raw = pickArray(entry, 'points') ?? [];
  return raw.filter(isObject).map((p) => ({
    time: pickNumber(p, 'time') ?? 0,
    value: pickNumber(p, 'value') ?? 0,
    curve: pickString(p, 'curve') ?? 'linear',
  }));
}

function pickBuses(src: Record<string, unknown>): Bus[] {
  const raw = pickArray(src, 'buses') ?? [];
  return raw.filter(isObject).map((entry) => ({
    id: pickString(entry, 'id') ?? 'unknown',
    name: pickString(entry, 'name') ?? 'Bus',
    color: pickString(entry, 'color') ?? '#888888',
    volume_db: pickNumber(entry, 'volume_db') ?? 0,
    pan: pickNumber(entry, 'pan') ?? 0,
    mute: pickBoolean(entry, 'mute') ?? false,
    solo: pickBoolean(entry, 'solo') ?? false,
    inserts: pickInserts(entry),
    sends: pickSends(entry),
    output_routing: pickString(entry, 'output_routing') ?? 'master',
  }));
}

function pickMaster(src: Record<string, unknown>): MasterChannel {
  const m = isObject(src['master']) ? src['master'] : EMPTY_MASTER;
  if (!isObject(m)) return { ...EMPTY_MASTER };
  return {
    id: pickString(m, 'id') ?? 'master',
    volume_db: pickNumber(m, 'volume_db') ?? 0,
    pan: pickNumber(m, 'pan') ?? 0,
    mute: pickBoolean(m, 'mute') ?? false,
    inserts: pickInserts(m),
  };
}

function pickMarkers(src: Record<string, unknown>): Marker[] {
  const raw = pickArray(src, 'markers') ?? [];
  return raw.filter(isObject).map((entry) => ({
    id: pickString(entry, 'id') ?? 'unknown',
    name: pickString(entry, 'name') ?? 'Marker',
    position_seconds:
      pickNumber(entry, 'position_seconds') ?? pickNumber(entry, 'time_seconds') ?? 0,
    ...(typeof entry['color'] === 'string' ? { color: entry['color'] as string } : {}),
  }));
}

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pickString(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  return typeof v === 'string' ? v : undefined;
}

function pickNumber(o: Record<string, unknown>, key: string): number | undefined {
  const v = o[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function pickBoolean(o: Record<string, unknown>, key: string): boolean | undefined {
  const v = o[key];
  return typeof v === 'boolean' ? v : undefined;
}

function pickArray(o: Record<string, unknown>, key: string): unknown[] | undefined {
  const v = o[key];
  return Array.isArray(v) ? v : undefined;
}

function pickStringArray(o: Record<string, unknown>, key: string): string[] | undefined {
  const v = o[key];
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : undefined;
}
